// /home/dsingh/qchat/qchat_fe/src/Component/SideData/Sidebar/SidebarMenu.js

import React, { useState, useEffect } from "react";
import sidebarStyle from "./UpdateSidebarMenu.module.css";
import appDetailStyle from "../Overview/AppDetail.module.css";
import { TbCategory } from "react-icons/tb";
import { menuGroups } from "./itemList";
import SidebarList from "./SidebarList";
import { useHistory } from "react-router-dom";
import { GrFormNext } from "react-icons/gr";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";

const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const atob_result = atob(base64);
    const jsonPayload = decodeURIComponent(
      atob_result
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

export const SidebarMenu = ({
  activeItemId,
  setActiveItemId,
  appData,
  toggleSidebar,
  mergedSettings,
  tenantPlan,
}) => {
  const [isRotated, setIsRotated] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const history = useHistory();

  // ============================================================
  // INITIALIZE EXPANDED GROUPS
  // ============================================================
  useEffect(() => {
    if (menuGroups.length > 0 && Object.keys(expandedGroups).length === 0) {
      const initialExpanded = {};
      // Expand the first group by default, collapse the rest
      menuGroups.forEach((group, index) => {
        initialExpanded[group.id] = index === 0;
      });
      setExpandedGroups(initialExpanded);
    }

    if (Object.keys(expandedSections).length === 0) {
      const initialSectionExpanded = {
        itemList: true,
        moderate: true,
        appSettings: true,
        shareDeveloper: true,
        whiteLabelDomains: true,
        globleSettings: true,
        inviteSuppUser: true,
      };
      setExpandedSections(initialSectionExpanded);
    }
  }, []);

  // ============================================================
  // PERMISSION CHECK
  // ============================================================
  const hasVisibleItems = (items) => {
    const token = localStorage.getItem("token");
    const decoded = token ? decodeJWT(token) : null;

    if (decoded?.is_support) {
      const userRoles = decoded.user_roles || [];
      return items.some(
        (item) =>
          !item.permitted_to ||
          item.permitted_to.some((role) => userRoles.includes(role)),
      );
    }
    return items.length > 0;
  };

  const isGroupActive = (groupItems) => {
    return groupItems.some((item) => item.text === activeItemId);
  };

  // ============================================================
  // HANDLE ITEM CLICK
  // ============================================================
  const handleItemClick = (item) => {
    if (item && item.target) {
      setActiveItemId(item.target.textContent);
    } else if (item && typeof item === "object") {
      setActiveItemId(item.text);
    } else if (typeof item === "string") {
      setActiveItemId(item);
    }
    toggleSidebar();
  };

  const handleToggle = () => {
    setIsRotated((prev) => !prev);
    toggleSidebar();
  };

  // ============================================================
  // TOGGLE GROUP (Accordion behavior - only one open at a time)
  // ============================================================
  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => {
      const isCurrentlyExpanded = prev[groupId] !== false;

      // If clicking the already-expanded group, collapse it
      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [groupId]: false,
        };
      }

      // Otherwise, collapse ALL groups and expand only the clicked one
      const newState = {};
      menuGroups.forEach((group) => {
        newState[group.id] = false;
      });
      newState[groupId] = true;
      return newState;
    });
  };

  // ============================================================
  // FILTER VISIBLE GROUPS BASED ON PERMISSIONS
  // ============================================================
  // Note: Feature flag filtering already happened in menuGroups.js
  // This is an additional filter based on user permissions
  const visibleGroups = menuGroups.filter((group) =>
    hasVisibleItems(group.items),
  );

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className={sidebarStyle.sidebar_wrapper}>
      <div className={sidebarStyle.sidebarListItm}>
        <div>
          <button
            className={`${appDetailStyle.sidebarBack} ${isRotated
                ? appDetailStyle.rotateOpen
                : appDetailStyle.rotateClosed
              }`}
            title="back"
            onClick={handleToggle}
          >
            <GrFormNext size={20} />
          </button>
        </div>

        <div>
          <div className={appDetailStyle.firstApp_created}>
            <div>
              <h3 className={appDetailStyle.app_name}>{appData.app_name}</h3>
              <p className={appDetailStyle.appUser_ids}>
                <span>App ID:</span>
                {appData.app_id}
              </p>
            </div>
          </div>

          <div className={appDetailStyle["sidebarList-data"]}>
            <div className={sidebarStyle.groupedMenu}>
              {visibleGroups.map((group) => {
                const isExpanded = expandedGroups[group.id] !== false;
                const visibleItems = group.items.filter((item) =>
                  hasVisibleItems([item]),
                );

                if (visibleItems.length === 0) return null;
                const isActive = isGroupActive(visibleItems);
                return (
                  <div
                    key={group.id}
                    className={sidebarStyle.menuGroup}
                    data-tour={group.dataTour}
                  >
                    <div
                      className={`${sidebarStyle.menuGroupHeader} ${isActive ? sidebarStyle.menuGroupActive : ""
                        }`}
                      onClick={() => toggleGroup(group.id)}
                    >
                      <span className={sidebarStyle.groupIcon}>
                        {group.icon}
                      </span>
                      <span className={sidebarStyle.groupLabel}>
                        {group.label}
                      </span>
                      <span className={sidebarStyle.groupArrow}>
                        {isExpanded ? (
                          <FaAngleUp size={14} />
                        ) : (
                          <FaAngleDown size={14} />
                        )}
                      </span>
                    </div>
                    {isExpanded && (
                      <div className={sidebarStyle.menuGroupItems}>
                        <SidebarList
                          items={visibleItems}
                          onItemClick={handleItemClick}
                          activeItemId={activeItemId}
                          appData={appData}
                          isGrouped={true}
                          showSteps={true}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className={appDetailStyle.apps}>
        <button
          className={appDetailStyle.btn}
          onClick={() => {
            history.push("/");
          }}
        >
          <TbCategory size={28} />
          Apps
        </button>
      </div>
    </div>
  );
};